import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import {
  API_URL,
  ILockboxDetailsAggregatorDto,
} from '@/dtos';
import { StatusNames } from '@/modules/employee/employee.types';
import {
  ClaimMatchingType,
  EmployeeMatchingType,
  Lockbox,
  LockboxActiveType,
  LockboxClaim,
  LockboxPaymentMethod,
  LockboxStatus,
  LockboxStatusReason,
  LockboxType,
  MatchedEmployee,
  WireStatus,
} from '@/modules/employer/components/Lockbox/lockbox.types';

const getFullStatusName = (firstLetter: keyof typeof StatusNames) => StatusNames[firstLetter];

interface LockboxOcrDetailsPayload {
  type?: string;
  address?: {
    line1?: string;
    city?: string;
    state?: string;
    zip?: string;
    zipcode?: string;
  };
  original_mailing_type?: string;
  statement_date?: string;
  check_number?: string;
  pay_to?: string;
  amount?: number;
}

export interface ContactDetailsPayload {
  contact_id?: number;
  first_name?: string;
  last_name?: string;
  role_name?: string;
}

interface Props {
  lockboxId: string;
}

const useGetLockboxQuery = ({ lockboxId }: Props) => {
  const { data, isLoading, isError, isFetching, refetch } = useQuery(
    [API_URL.LOCKBOXES_BY_LOCKBOX_ID(lockboxId)],
    () => api.get<ILockboxDetailsAggregatorDto>(API_URL.LOCKBOXES_BY_LOCKBOX_ID(lockboxId))
  );

  const formattedData = useMemo(
    (): Lockbox => ({
      id: toString(data?.data?.id),
      accountId: toString(data?.data?.account_id),
      // TODO: this field is missing
      // @ts-ignore
      address: data?.data?.address,
      amount: toString(data?.data?.amount),
      birthDate: data?.data?.birth_date,
      contactId: toString(data?.data?.contact_id),
      requestDate: data?.data?.request_date,
      documentId: toString(data?.data?.document_id),
      employeeId: toString(data?.data?.employee_id),
      employeeName: data?.data?.employee_name,
      accountNumber: data?.data?.account_number,
      lockboxStatus: data?.data?.lockbox_status as unknown as LockboxStatus,
      lockboxType: data?.data?.lockbox_type as unknown as LockboxType,
      name: data?.data?.name,
      formName: data?.data?.form_name,
      ssn: data?.data?.ssn,
      reason: data?.data?.reason as unknown as LockboxStatusReason,
      notes: data?.data?.notes,
      updatedAt: data?.data?.updated_at as unknown as string,
      liquidateAllFunds: data?.data?.liquidate_all_funds,
      wireStatusType: data?.data?.wire_status_type as unknown as WireStatus,
      paymentMethodType: data?.data?.payment_method_type as unknown as LockboxPaymentMethod,
      archiveType: data?.data?.archive_type as unknown as LockboxActiveType,
      isMultipleRecordsWarning:
        data?.data?.is_multiple_records &&
        (data?.data?.lockbox_status as unknown as LockboxStatus) === LockboxStatus.NEW,
      parentId: toString(data?.data?.parent_id),
      checkId: toString(data?.data?.check_id),
      ocr: data?.data?.ocr
        ? {
            accountNumber: data?.data?.ocr?.account_number,
            employeeName: data?.data?.ocr?.employee_name,
            claimIds: data?.data?.ocr?.claim_ids?.map((claimId) => toString(claimId)),
            ssn: data?.data?.ocr?.ssn,
            birthDate: data?.data?.ocr?.birth_date,
            amount: toString(data?.data?.ocr?.amount),
            // TODO: this field is missing
            // @ts-ignore
            date: data?.data?.ocr?.date,
            address: {
              addressLine1: data?.data?.ocr?.address?.street,
              addressLine2: data?.data?.ocr?.address?.house_number_or_name,
              city: data?.data?.ocr?.address?.city,
              state: data?.data?.ocr?.address?.state_or_province,
              zip: data?.data?.ocr?.address?.postal_code,
            },
            details: data?.data?.ocr?.details
              ? (() => {
                  const ocrDetails = data?.data?.ocr?.details as unknown as LockboxOcrDetailsPayload;
                  return {
                    address: ocrDetails?.address
                      ? {
                          line1: ocrDetails.address.line1,
                          city: ocrDetails.address.city,
                          state: ocrDetails.address.state,
                          zipcode: ocrDetails.address.zip || ocrDetails.address.zipcode,
                        }
                      : undefined,
                    originalMailingType: ocrDetails?.original_mailing_type,
                    checkNumber: ocrDetails?.check_number,
                    payTo: ocrDetails?.pay_to,
                    amount: ocrDetails?.amount,
                  };
                })()
              : undefined,
          }
        : undefined,
      details: data?.data?.details
        ? {
            type: data?.data?.details?.type,
            // @ts-ignore
            originalMailingType: data?.data?.details?.original_mailing_type,
            // @ts-ignore
            resendStatus: data?.data?.details?.resend_status,
            // @ts-ignore
            processingAction: data?.data?.details?.processing_action,
            // @ts-ignore
            processingDecision: data?.data?.details?.processing_decision
              ? {
                // @ts-ignore
                type: data?.data?.details?.processing_decision?.type,
                // @ts-ignore
                reason: data?.data?.details?.processing_decision?.reason,
              }
              : undefined,
          }
        : undefined,
      checkDetails: data?.data?.check_details
        ? {
            addressLine1: data?.data?.check_details?.address_line_1,
            addressLine2: data?.data?.check_details?.address_line_2,
            benefitOf: data?.data?.check_details?.benefit_of,
            checkMemo: data?.data?.check_details?.check_memo,
            checkName: data?.data?.check_details?.check_name,
            city: data?.data?.check_details?.city,
            id: toString(data?.data?.check_details?.id),
            state: data?.data?.check_details?.state,
            vendorId: toString(data?.data?.check_details?.vendor_id),
            vendorName: data?.data?.check_details?.vendor_name,
            zip: data?.data?.check_details?.zip,
          }
        : undefined,
      matchEmployees: data?.data?.match_employees?.map(
        (matchedEmployee): MatchedEmployee => ({
          employeeId: toString(matchedEmployee?.employee_id),
          employeeName: matchedEmployee?.employee_name,
          ssn: matchedEmployee?.ssn,
          birthDate: matchedEmployee?.birth_date,
          employmentStatus: getFullStatusName(matchedEmployee?.employment_status as keyof typeof StatusNames),
          organizationId: toString(matchedEmployee?.organization_id),
          organizationName: matchedEmployee?.organization_name,
          accountId: toString(matchedEmployee?.account_id),
          accountNumber: matchedEmployee?.account_number,
          availableAmount: matchedEmployee?.available_amount,
          investmentsBalance: matchedEmployee?.investments_balance,
          accountStatus: matchedEmployee?.account_status,
          investmentStatus: matchedEmployee?.investment_status,
          labelTypes: matchedEmployee?.label_types as unknown as EmployeeMatchingType[],
          mailingAddress: matchedEmployee?.mailing_address
            ? {
                line1: matchedEmployee?.mailing_address?.line1,
                line2: matchedEmployee?.mailing_address?.line2 ?? undefined,
                city: matchedEmployee?.mailing_address?.city,
                state: matchedEmployee?.mailing_address?.state,
                zipcode: matchedEmployee?.mailing_address?.zipcode,
              }
            : undefined,
        })
      ),
      contactDetails: data?.data?.contact_details
        ? {
            contactId: toString(data?.data?.contact_details?.contact_id),
            firstName: data?.data?.contact_details?.first_name,
            lastName: data?.data?.contact_details?.last_name,
            role: data?.data?.contact_details?.role_name,
          }
        : undefined,
      childRecords: data?.data?.child_records?.map((childRecord) => ({
        id: toString(childRecord?.id),
        main: childRecord?.main,
        name: childRecord?.name,
        pages: childRecord?.pages,
      })),
      claimRepayments: data?.data?.claim_repayments
        ?.map((claimRepayment) => ({
          claimId: toString(claimRepayment?.claim_id),
          status: claimRepayment?.status,
          repaymentAmount: toString(claimRepayment?.repayment_amount),
        }))
        ?.filter((claimRepayment) => !claimRepayment?.status),
      createdClaimRepayments: data?.data?.claim_repayments
        ?.map((claimRepayment) => ({
          claimId: toString(claimRepayment?.claim_id),
          status: claimRepayment?.status,
          repaymentAmount: toString(claimRepayment?.repayment_amount),
        }))
        ?.filter((createdRepayment) => !!createdRepayment?.status),
      ...(data?.data?.remainder
        ? {
            remainder: {
              destinationType: data?.data?.remainder?.destination_type,
              accountId: toString(data?.data?.remainder?.account_id),
              benefitOf: data?.data?.remainder?.benefit_of,
              addressLine1: data?.data?.remainder?.address_line1,
              addressLine2: data?.data?.remainder?.address_line2,
              city: data?.data?.remainder?.city,
              state: data?.data?.remainder?.state,
              zip: data?.data?.remainder?.zip,
            },
          }
        : {}),
      suggestedClaims: data?.data?.match_employees
        ?.filter((matchedEmployee) => !!matchedEmployee?.claim_id)
        ?.map(
          (matchedEmployee): LockboxClaim => ({
            claimId: toString(matchedEmployee?.claim_id),
            employeeId: toString(matchedEmployee?.employee_id),
            employeeName: matchedEmployee?.employee_name,
            labelTypes: matchedEmployee?.label_types as unknown as ClaimMatchingType[],
          })
        ),
    }),
    [data]
  );

  return {
    formattedData,
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  };
};

export default useGetLockboxQuery;
